/**
 * @file appData.ts
 * @description
 * -----------------------------------------------------
 * Definición de constantes globales de la aplicación
 * -----------------------------------------------------
 * @version 1.0.0a
 * @created 2024-05-12
 * @modified 2025-11-03
 * @author DLTCode
 * @copyright © DLTCode
 * @license Private
 *
 * */

import { Pathname } from "@/features/i18n";
import { themedAsset, ThemedAsset } from "@/constants/themedAsset";

/**
 * Información de contacto de la empresa.
 */
interface ContactInfo {
  /** Dirección de correo electrónico */
  email: string;
  /** Número de teléfono */
  phone: string;
  /** URL del perfil de LinkedIn */
  linkedIn: string;
}

/**
 * Enlaces a las políticas legales del sitio.
 */
interface LegalLinks {
  /** URL de la política de cookies */
  cookies: string;
  /** URL de la política de privacidad */
  privacy: string;
}

/**
 * Estructura de datos RAW de la aplicación (con ThemedAssets sin resolver).
 */
export interface AppDataRaw {
  redirectAfterRegisterPathname: Pathname;
  /** Configuración de marca y logos */
  branding: {
    /** Logo principal de la marca */
    logo: ThemedAsset;
  };
  /** Información de contacto */
  contact: ContactInfo;
  /** Enlaces legales */
  legal: LegalLinks;
  /** Contenido del pie de página */
  footer: {
    /** Logos de organizaciones y certificaciones */
    logos: {
      nextGenEU: ThemedAsset;
      minTdfp: ThemedAsset;
      prtr: ThemedAsset;
      incibe: ThemedAsset;
      iso9001: ThemedAsset;
      iso27001: ThemedAsset;
      ens: ThemedAsset;
      validacionDnsh: ThemedAsset;
      uneEn301549: ThemedAsset;
    };
  };
}

/**
 * Estructura de datos RESUELTA de la aplicación (con rutas de activos ya resueltas según el tema).
 */
export interface AppDataResolved {
  redirectAfterRegisterPathname: Pathname;
  /** Configuración de marca y logos */
  branding: {
    /** Logo principal de la marca */
    logo: string;
  };
  /** Información de contacto */
  contact: ContactInfo;
  /** Enlaces legales */
  legal: LegalLinks;
  /** Contenido del pie de página */
  footer: {
    /** Logos de organizaciones y certificaciones */
    logos: {
      nextGenEU: string;
      minTdfp: string;
      prtr: string;
      incibe: string;
      iso9001: string;
      iso27001: string;
      ens: string;
      validacionDnsh: string;
      uneEn301549: string;
    };
  };
}

/**
 * Datos principales de la aplicación.
 * Incluye configuración de marca, contacto, enlaces legales y contenido del footer.
 */
export const APP_DATA = {
  redirectAfterRegisterPathname: "/login",
  branding: {
    logo: themedAsset({
      path: "svg",
      filename: "brand",
      extension: "svg",
    }),
  },
  contact: {
    email: "info@dltcode.es",
    phone: "+34931311540",
    linkedIn: "https://www.linkedin.com/company/dlt-code",
  },
  legal: {
    cookies: "https://dltcode.es/politica-cookies",
    privacy: "https://dltcode.es/politica-privacidad",
  },
  footer: {
    logos: {
      nextGenEU: themedAsset({
        path: "brands",
        filename: "next-gen-eu",
        extension: "svg",
      }),
      minTdfp: themedAsset({
        path: "brands",
        filename: "min-tdfp",
        extension: "svg",
      }),
      prtr: themedAsset({ path: "brands", filename: "prtr", extension: "svg" }),
      incibe: themedAsset({
        path: "brands",
        filename: "incibe",
        extension: "svg",
      }),
      iso9001: themedAsset({
        path: "brands",
        filename: "iso9001",
        extension: "svg",
      }),
      iso27001: themedAsset({
        path: "brands",
        filename: "iso27001",
        extension: "svg",
      }),
      ens: themedAsset({
        path: "brands",
        filename: "ens",
        extension: "svg",
        hasDarkVariant: false,
      }),
      validacionDnsh: themedAsset({
        path: "brands",
        filename: "validacion-dnsh",
        extension: "svg",
      }),
      uneEn301549: themedAsset({
        path: "brands",
        filename: "une-en-301549",
        extension: "svg",
      }),
    },
  },
} as const satisfies AppDataRaw;
